import cn from 'classnames';
import { find } from 'lodash';
import moment from 'moment';
import { observer } from 'mobx-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, RadioChangeEvent } from 'antd';

import { Input } from '@/comp/pages/EnginePage/Engine/Common/styled';
import { Radio } from '@/comp/pages/EnginePage/Common/styled';
import { SelectContainer } from '@/comp/pages/EnginePage/Engine/Sensors/styled';
import DeleteItemPopconfirmIcon from '@/comp/pages/EnginePage/Engine/Common/DeleteItemPopconfirmIcon/DeleteItemPopconfirmIcon';
import ValidationTooltip from '@/comp/pages/EnginePage/Engine/Common/ValidationTooltip/ValidationTooltip';
import { useStores } from '@/hooks/use-stores';
import { EngineStepsEnum } from '@/store/Engine/constants';
import { maxLength, onlyDigits } from '@/utils/util-functions';
import { EPButton, EPSelect } from '@/components';
import { ValidationMessage } from '@/types';

import DragAndDropList from './DragAndDropList';
import RepeatOnSwitch from './RepeatOnSwitch';
import {
  CompressorsGroupContainer,
  PopoverGlobalStyle,
  RotationInfoPopoverContainer,
  SelectDropdownStyle,
} from './styled';
import { EngineStore } from '@/store/Engine/EngineStore';
import DevicesStore from '@/store/Devices/DevicesStore';

const hoursArray = Array.from({ length: 24 }, (_, i) => i);
const timeOptions = hoursArray.map(hour => ({ value: moment({ hour }).format('h:mm a') }));

interface RotationInfoPopoverProps {
  compressorsGroup: {
    compressors: unknown[];
    rotation: boolean;
    id: string;
    repeatEvery: string;
    repeatOn: string;
    time: string;
  };
  onGroupFieldChange: (compressorGroupId: string, field: string) => (e: string | RadioChangeEvent) => void;
  validationMessages: ValidationMessage[];
  t: (key: string) => string;
  hasTriedToSubmit: boolean;
}

const RotationInfoPopover = ({
  compressorsGroup,
  onGroupFieldChange,
  validationMessages,
  t,
  hasTriedToSubmit,
}: RotationInfoPopoverProps) => {
  const isRotationDisabled = (compressorsGroup?.compressors?.length || 0) < 2;

  const onRepeatEveryKeyPress = event => onlyDigits(event);

  const onInputScroll = e => e?.target?.blur();

  return (
    <RotationInfoPopoverContainer>
      <div className="compressors-group-info">
        <span className="compressors-group-info-label">{t('rotation')}</span>
        <Radio.Group
          value={compressorsGroup?.rotation}
          onChange={onGroupFieldChange(compressorsGroup?.id, 'rotation')}
          disabled={isRotationDisabled}
        >
          <Radio value $isViewMode={isRotationDisabled}>
            {t('On')}
          </Radio>
          <Radio value={false} $isViewMode={isRotationDisabled}>
            {t('Off')}
          </Radio>
        </Radio.Group>
      </div>
      <div className="compressors-group-info">
        <div className="compressors-group-info-label">{t('repeatEvery')}</div>
        <ValidationTooltip
          message={
            hasTriedToSubmit && find(validationMessages, { key: compressorsGroup?.id, field: 'repeatEvery' })?.message
          }
        >
          <Input
            bordered
            value={compressorsGroup?.repeatEvery}
            onChange={onGroupFieldChange(compressorsGroup?.id, 'repeatEvery')}
            onWheel={onInputScroll}
            count={maxLength(2)}
            onKeyPress={onRepeatEveryKeyPress}
            disabled={!compressorsGroup?.rotation}
          />
        </ValidationTooltip>
        <span className="compressors-group-info-label">
          {t(`week${+compressorsGroup?.repeatEvery === 1 ? '' : 's'}`)}
        </span>
      </div>
      <div className="compressors-group-info">
        <div className="compressors-group-info-label">{t('repeatOn')}</div>
        <RepeatOnSwitch
          value={compressorsGroup?.repeatOn}
          onChange={onGroupFieldChange(compressorsGroup?.id, 'repeatOn')}
          disabled={!compressorsGroup?.rotation}
        />
      </div>
      <div className="compressors-group-info">
        <div className="compressors-group-info-label">{t('time')}</div>
        <EPSelect
          value={{ value: compressorsGroup?.time }}
          options={timeOptions}
          onChange={onGroupFieldChange(compressorsGroup?.id, 'time')}
          className={cn('time-select', !compressorsGroup?.rotation && 'disabled')}
          disabled={!compressorsGroup?.rotation}
        />
      </div>
    </RotationInfoPopoverContainer>
  );
};

const CompressorsGroup = observer(({ hasTriedToSubmit }: { hasTriedToSubmit: boolean }) => {
  const { t } = useTranslation();
  const { engineStore, devicesStore }: { engineStore?: EngineStore; devicesStore?: DevicesStore } = useStores();
  const { compressorGroups, validationMessages } = engineStore;

  const onAddGroupClick = () => compressorGroups?.addCompressorGroup();

  const onGroupFieldChange = (compressorGroupId, field) => e => {
    compressorGroups?.setCompressorGroupField(compressorGroupId, field, e?.target?.value ?? e);
    if (hasTriedToSubmit)
      engineStore.validate(EngineStepsEnum.CompressorGroups, null, null, `${field}-${compressorGroupId}`);
  };

  const onDeleteGroupClick = compressorGroupId => () => compressorGroups?.deleteCompressorGroup(compressorGroupId);

  return (
    <CompressorsGroupContainer>
      <div className="compressor-groups">
        {compressorGroups?.compressorsGroups?.map(compressorsGroup => (
          <div className={cn('compressor-group', engineStore.isReadOnly && 'disabled')} key={compressorsGroup?.id}>
            <div className="compressor-group-title">
              <Input
                value={compressorsGroup?.name}
                bordered={false}
                onChange={onGroupFieldChange(compressorsGroup?.id, 'name')}
                className="compressor-group-name"
              />
              <div className="rotation-options">
                <span>{t('rotation')}&nbsp;</span>
                <PopoverGlobalStyle />
                <Popover
                  content={
                    <RotationInfoPopover
                      compressorsGroup={compressorsGroup}
                      onGroupFieldChange={onGroupFieldChange}
                      validationMessages={validationMessages}
                      t={t}
                      hasTriedToSubmit={hasTriedToSubmit}
                    />
                  }
                  overlayClassName="rotation"
                  placement="bottom"
                  trigger="click"
                >
                  <span className="rotation-value">{compressorsGroup?.rotation ? t('on') : t('off')}</span>
                </Popover>
                <DeleteItemPopconfirmIcon
                  itemName={compressorsGroup?.name}
                  onConfirm={onDeleteGroupClick(compressorsGroup?.id)}
                  className="delete-item-icon"
                />
              </div>
            </div>
            <SelectContainer>
              <SelectDropdownStyle />
              <DragAndDropList
                compressorGroupId={compressorsGroup?.id}
                hasTriedToSubmit={hasTriedToSubmit}
                compressorsGroup={compressorsGroup}
              />
            </SelectContainer>
          </div>
        ))}
      </div>
      {!engineStore.isReadOnly && (
        <EPButton
          type="primary"
          size="medium"
          onClick={onAddGroupClick}
          className="add-compressor-group-button"
          disabled={devicesStore?.compressors?.every(compressor =>
            engineStore.compressorGroups.isCompressorInAnyGroup(compressor?.id),
          )}
        >
          <span className="button-label">{t('addCompressorGroup')}</span>
        </EPButton>
      )}
    </CompressorsGroupContainer>
  );
});

export default CompressorsGroup;
